import { Label, Switch } from '@playcastdotio/Shadcn';
import { useEffect, useState, useCallback } from 'react';
import { PlaycastReactFunctions } from '../PlaycastWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { DiscordAuthButton } from '@playcastdotio/OAuthButtons';
import constants from '@playcastdotio/GlobalConfig';

export const twitchMessages = {
  twitchSendStreamUrlReminder: 'A Playcast session is live! To join in and play, click this link:',
  twitchSendStartAnnouncement: 'has started a new Playcast session! To join the Playcast and play together, click this link:',
  twitchSendEndAnnouncement: 'The Playcast session has concluded. Thanks to everyone who joined the fun!',
  twitchSendPlaycastInfo:
    'Playcast lets you share and play your PC games with your friends, no matter what device they’re on. Download it here to start hosting games for your friends!',
  twitchRespondToChatCommand: 'Join from this link: ',
};

const headerText =
  'Automatically send Playcast invites through your Twitch/Discord chat and seamlessly grow your player community with zero effort - no manual invites needed';

export const Tools = ({ context }: { context: PlaycastReactFunctions }) => {
  console.log('context.stateStores.discordSettingsState', context.stateStores.discordSettingsState);
  const [twitchSettings, setTwitchSettings] = useState(context.stateStores.twitchSettingsState);
  const [discordSettings, setDiscordSettings] = useState(context.stateStores.discordSettingsState);
  const [remoteControlConnected, setRemoteControlConnected] = useState(
    context.electronFunctions ? true : context.stateStores.functionConnectionState.connected,
  );

  const updateTwitchSettings = useCallback(
    (updates: Partial<typeof twitchSettings>) => {
      const newSettings = { ...twitchSettings, ...updates };
      setTwitchSettings(newSettings);
      context.targetFunctions.updateTwitchSettings(newSettings);
    },
    [twitchSettings, context.targetFunctions],
  );

  const twitchBotToggle = useCallback(() => {
    updateTwitchSettings({
      isTwitchBotEnabled: !twitchSettings.isTwitchBotEnabled,
      isSendStartAnnouncement: !twitchSettings.isTwitchBotEnabled,
      isSendStreamUrlReminder: !twitchSettings.isTwitchBotEnabled,
      isSendEndAnnouncement: !twitchSettings.isTwitchBotEnabled,
      isSendPlaycastInfo: !twitchSettings.isTwitchBotEnabled,
      shouldRespondToChat: !twitchSettings.isTwitchBotEnabled,
    });
  }, [twitchSettings.isTwitchBotEnabled, updateTwitchSettings]);

  const updateDiscordSettings = useCallback(
    (updates: Partial<typeof discordSettings>) => {
      const newSettings = { ...discordSettings, ...updates };
      setDiscordSettings(newSettings);
      context.targetFunctions.updateDiscordSettings(newSettings);
    },
    [discordSettings, context.targetFunctions],
  );

  const discordBotToggle = useCallback(() => {
    updateDiscordSettings({
      isDiscordBotEnabled: !discordSettings.isDiscordBotEnabled,
    });
  }, [discordSettings.isDiscordBotEnabled, updateDiscordSettings]);

  useEffect(() => {
    setTwitchSettings(context.stateStores.twitchSettingsState);
  }, [context.stateStores.twitchSettingsState]);

  useEffect(() => {
    setDiscordSettings(context.stateStores.discordSettingsState);
  }, [context.stateStores.discordSettingsState]);

  useEffect(() => {
    setRemoteControlConnected(context.electronFunctions ? true : context.stateStores.functionConnectionState.connected);
  }, [context.electronFunctions, context.stateStores.functionConnectionState.connected]);

  /**
   * Generate FAQ URL based on current environment
   */
  const generateFaqUrl = useCallback((): string => {
    return constants.PAVILION_PLAYER_BASE_URL + 'faqs#how-to-set-up-bots';
  }, []);

  /**
   * Generate player base URL based on current environment and invitation type
   */
  const generatePlayerBaseUrl = useCallback((): string => {
    const invitationType = context.stateStores.invitationStateV2.type;
    // Determine if this is a Pavilion (cafe) or Playcast (lobby) URL
    const isPavilion = invitationType === 'cafe';

    if (isPavilion) {
      return constants.PAVILION_PLAYER_BASE_URL;
    } else {
      return constants.PRIME_PLAYER_BASE_URL;
    }
  }, [context.stateStores.invitationStateV2.type]);

  const handleFaqClick = useCallback(() => {
    // Open the FAQ page and scroll to the "How to Set Up Bots" section
    const faqUrl = generateFaqUrl();
    window.open(faqUrl, '_blank', 'noopener,noreferrer');
  }, [generateFaqUrl]);

  const twitchFunctions = [
    {
      id: 'start-announcement-toggle',
      label: 'Start Announcement',
      checked: twitchSettings.isSendStartAnnouncement,
      onCheckChange: () => updateTwitchSettings({ isSendStartAnnouncement: !twitchSettings.isSendStartAnnouncement }),
      message: twitchMessages['twitchSendStartAnnouncement'],
    },
    {
      id: 'stream-url-reminder-toggle',
      label: 'Stream URL Reminder',
      checked: twitchSettings.isSendStreamUrlReminder,
      onCheckChange: () => updateTwitchSettings({ isSendStreamUrlReminder: !twitchSettings.isSendStreamUrlReminder }),
      message: `${twitchMessages['twitchSendStreamUrlReminder']} ${generatePlayerBaseUrl()}j/invite-code`,
    },
    {
      id: 'end-announcement-toggle',
      label: 'End Announcement',
      checked: twitchSettings.isSendEndAnnouncement,
      onCheckChange: () => updateTwitchSettings({ isSendEndAnnouncement: !twitchSettings.isSendEndAnnouncement }),
      message: twitchMessages['twitchSendEndAnnouncement'],
    },
    {
      id: 'push-notification-toggle',
      label: 'Push Notification',
      checked: twitchSettings.isSendPlaycastInfo,
      onCheckChange: () => updateTwitchSettings({ isSendPlaycastInfo: !twitchSettings.isSendPlaycastInfo }),
      message: `${twitchMessages['twitchSendPlaycastInfo']} ${generatePlayerBaseUrl()}`,
    },
    {
      id: 'respond-to-chat-toggle',
      label: 'Respond to !join command',
      checked: twitchSettings.shouldRespondToChat,
      onCheckChange: () => updateTwitchSettings({ shouldRespondToChat: !twitchSettings.shouldRespondToChat }),
      message: `${twitchMessages['twitchRespondToChatCommand']} ${generatePlayerBaseUrl()}j/invite-code`,
    },
  ];

  if (!remoteControlConnected) {
    return (
      <div data-testid='content-root' className='flex flex-1 flex-col gap-4 text-white'>
        <div className='flex items-center justify-between'>
          <Label className='font-[Syne] text-3xl'>Tools</Label>
          <button
            onClick={handleFaqClick}
            className='flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors'
            data-testid='tools-faq-button'
          >
            <FontAwesomeIcon icon={faCircleQuestion} className='size-4 text-gray-600' />
            <span className='text-gray-600'>FAQ</span>
          </button>
        </div>
        <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
          <div className='text-sm text-gray-400'>{headerText}</div>
          <div className='flex items-center gap-2 pt-4 text-sm text-yellow-400'>
            <FontAwesomeIcon icon={faExclamationTriangle} size='lg' />
            <span>You are not currently connected to a host. Please connect to change these settings.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid='content-root' className='flex flex-1 flex-col gap-4 text-white'>
      <div className='flex items-center justify-between'>
        <Label className='font-[Syne] text-3xl'>Tools</Label>
        <button
          onClick={handleFaqClick}
          className='flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors'
          data-testid='tools-faq-button'
        >
          <FontAwesomeIcon icon={faCircleQuestion} className='size-4 text-gray-600' />
          <span className='text-gray-600'>FAQ</span>
        </button>
      </div>
      {context.stateStores.twitchSettingsState.twitchDisplayName ? (
        <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
          <div className='text-sm text-gray-400'>{headerText}</div>
          <div className='flex items-center'>
            <div>
              <div className='flex items-center gap-2'>
                <Switch
                  id='chat-bot-toggle'
                  checked={twitchSettings.isTwitchBotEnabled}
                  onCheckedChange={twitchBotToggle}
                  className='data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
                />
                <Label htmlFor='chat-bot-toggle' className='text-md cursor-pointer'>
                  Twitch Chatbot Enabled
                </Label>
              </div>
            </div>
          </div>
          {twitchFunctions.map(({ id, label, onCheckChange, checked, message }) => (
            <div key={id} className='flex rounded-lg border border-gray-500/75 px-2 py-2'>
              <div className='flex items-center gap-2'>
                <Switch
                  id={id}
                  checked={checked}
                  onCheckedChange={onCheckChange}
                  className='data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
                />
                <Label htmlFor={id} className='text-md cursor-pointer'>
                  {label}
                </Label>
              </div>
              <div className='bg-midnight-white ml-auto w-2/4 rounded-lg p-2 text-xs text-black'>
                <span className='text-midnight-bright font-semibold'>{context.stateStores.twitchSettingsState.twitchDisplayName}: </span>
                {message}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
          <div className='flex items-center gap-2 pt-4 text-sm text-yellow-400'>
            <FontAwesomeIcon icon={faExclamationTriangle} size='lg' />
            <span>Connect your Twitch account under the "Profile" tab to enable the Twitch Chatbot.</span>
          </div>
        </div>
      )}
      {context.stateStores.discordSettingsState.discordDisplayName ? (
        <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
          <div className='flex items-center'>
            <div>
              <div className='flex items-center gap-2'>
                <Switch
                  id='discord-chat-bot-toggle'
                  checked={discordSettings.isDiscordBotEnabled}
                  onCheckedChange={discordBotToggle}
                  className='data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
                />
                <Label htmlFor='discord-chat-bot-toggle' className='text-md cursor-pointer'>
                  Discord Chatbot Enabled
                </Label>
              </div>
            </div>
            <div className='ml-auto'>
              <DiscordAuthButton />
            </div>
          </div>
        </div>
      ) : (
        <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
          <div className='flex items-center gap-2 pt-4 text-sm text-yellow-400'>
            <FontAwesomeIcon icon={faExclamationTriangle} size='lg' />
            <span>Connect your Discord account under the "Profile" tab to enable the Discord Chatbot.</span>
          </div>
        </div>
      )}
    </div>
  );
};
